import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";

/**
 * Texte des Kalenders (Terminebene): der Bereich „Kalender“ (D23) und die Einstellungsseite „Einstellungen › Kalender“
 * (D27, unter `settings`). Die langen Wochentags- und Monatsnamen teilt der Kalender mit der Vorlesestimme der Einträge
 * (`DUE_ITEM_TEXT`).
 */
const entries = (n: number): string => (n === 0 ? "keine Einträge" : n === 1 ? "1 Eintrag" : `${n} Einträge`);

export const CALENDAR_TEXT = {
  // ── Bereich Kalender (D23) ────────────────────────────────────────────────────────────────────────────────────
  area: "Kalender",
  toolbarName: "Werkzeuge des Kalenders",
  company: "Unternehmen",
  allCompanies: "Alle",
  tickets: "Tickets",
  mine: "Meine",
  all: "Alle",
  newMilestone: "+ Meilenstein",
  periodGroup: "Zeitraum",
  viewChoice: "Sicht",
  views: { DAY: "Tag", WEEK: "Woche", MONTH: "Monat", YEAR: "Jahr" },
  today: "Heute",
  back: "Zurück",
  forward: "Weiter",
  /** Montag zuerst (ISO 8601). */
  weekdaysShort: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
  weekdaysLong: DUE_ITEM_TEXT.weekdaysLong,
  months: DUE_ITEM_TEXT.months,
  /** Kopf der leisen Spalte im Monatsraster. */
  calendarWeek: "KW",
  /** Zeitraumtexte der Leiste („Do 05.11.2026“, „KW 45 · 02.–08.11.2026“, „November 2026“, „2026“); `day` auch für den
   *  Kopf einer Wochenspalte („Mo 02.11.“). */
  period: {
    day: (weekday: string, date: string): string => `${weekday} ${date}`,
    week: (kw: number, range: string): string => `KW ${kw} · ${range}`,
    month: (month: string, year: number): string => `${month} ${year}`,
  },
  /** Überlauf-Knopf der Monatszelle (Hausregel 547, Entscheid 103): „mehr“, die Anzahl steht aufrecht daneben. */
  more: "mehr",
  /** Zählung im Sofort-Popup eines Jahrestags (ein Kennzeichen, kein Knopf). */
  moreCount: (n: number): string => `+${n} weitere`,
  overdueBlock: "Überfällig",
  empty: "Keine Termine.",
  loadError: "Der Kalender konnte nicht geladen werden.",
  /** Die Antwort ist gekürzt (höchstens 5 000 Einträge, D10). */
  limit: "Nicht alle Termine geladen – bitte Filter eingrenzen.",
  entries,
  /** Vorlesestimme eines Tages: „Donnerstag, 5. November, 3 Einträge, 1 überfällig“. */
  daySpeak: (weekday: string, day: number, month: string, n: number, overdue: number): string =>
    [`${weekday}, ${day}. ${month}`, entries(n), overdue > 0 ? `${overdue} überfällig` : ""].filter((t) => t !== "").join(", "),
  /** G1 (Hausregel 546): der Zustand zusätzlich zur Farbe als Zeichen — an den Einträgen in Woche und Monat, an den Tagen
   *  des Jahres, am Zähler des Telefons und in der Legende (dort mit dem Namen im Sofort-Popup). */
  stateMarks: { OVERDUE: "!", DUE_TODAY: "●", OPEN: "○", DONE: "✓" },
  stateNames: { OVERDUE: "überfällig", DUE_TODAY: "heute fällig", OPEN: "offen", DONE: "erledigt" },
  legendName: "Legende des Kalenders",
  /** Sofort-Popup eines Tages im Jahr: Kopf „Do 05.11.2026 · 3 Einträge“. */
  yearDayHead: (day: string, n: number): string => `${day} · ${entries(n)}`,

  settings: {
    loading: "Lädt …",
    loadFailed: "Die Einstellungen konnten nicht geladen werden.",
    saveFailed: "Speichern ist nicht gelungen.",
    invalid: "Die Einstellungen ließen sich so nicht speichern — bitte die Angaben prüfen.",
    changedElsewhere: "Die Einstellungen wurden inzwischen auf einem anderen Gerät geändert — der aktuelle Stand ist neu geladen.",
    writeFailed: "Die Änderung am Abonnement ist nicht gelungen.",
    unavailable: "Kalender-Abonnements sind auf diesem System nicht eingerichtet.",
    save: "Speichern",
    cancel: "Abbrechen",

    subscription: "Abonnement",
    notSetUp: "nicht eingerichtet",
    setUpSince: (day: string): string => `eingerichtet seit ${day}`,
    secretNotice: "Die Adresse ist ein Geheimnis: Wer sie kennt, liest die Termine ohne Anmeldung mit, und Cloud-Kalender "
      + "speichern den Inhalt auf ihren Servern. Die Adresse nicht weitergeben; bei Verdacht eine neue Adresse erzeugen.",
    personal: "Meine Termine",
    teamRow: (name: string): string => `Team ${name}`,
    copy: "Adresse kopieren",
    copyName: (row: string): string => `Adresse kopieren: ${row}`,
    copied: "Adresse kopiert.",
    copyFailed: "Kopieren nicht möglich — die Adresse bitte von Hand markieren und kopieren.",
    open: "Im Kalender öffnen",
    openName: (row: string): string => `Im Kalender öffnen: ${row}`,
    create: "Abonnement einrichten",
    rotate: "Neue Adresse erzeugen",
    remove: "Abonnement abschalten",
    confirmCreateTitle: "Abonnement einrichten?",
    confirmCreate: "Wer die Adresse kennt, sieht die Termine.",
    confirmRotateTitle: "Neue Adresse erzeugen?",
    confirmRotate: "Die bisherige Adresse hört sofort auf zu funktionieren.",
    confirmRemoveTitle: "Abonnement abschalten?",
    confirmRemove: "Die Adresse hört sofort auf zu funktionieren; Kalender, die sie abonniert haben, bekommen keine Termine mehr.",

    content: "Inhalt",
    alwaysIncluded: "Eigene Tickets mit Fälligkeit und Wiedervorlage sind immer enthalten.",
    sources: "Weitere Quellen",

    display: "Darstellung",
    layout: "Darstellung im Kalender",
    layoutAllDay: "Ganztägig am Fälligkeitstag",
    layoutSlotted: "Nacheinander im Raster",
    slotMinutes: "Raster",
    slotMinutesOption: (n: number): string => `${n} min`,
    slotStart: "Beginn",

    move: "Verschieben im Kalender",
    moveBehavior: "Beim Verschieben im Kalender",
    moveResubmit: "Neue Wiedervorlage",
    moveDue: "Fälligkeit verschieben",
    moveNotice: "Wirkt, sobald Kundeninfo Termine aus dem Kalender zurückliest (Stufe K2).",
  },
} as const;
